import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: t('common.error'),
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      const data = await response.json();
      
      setIsSubscribed(true);
      setEmail('');
      
      toast({
        title: t('common.success'),
        description: data.status === 'exists' 
          ? "You're already subscribed to our newsletter." 
          : "Thank you for subscribing to our newsletter.",
      });
      
    } catch (error) {
      toast({
        title: t('common.error'),
        description: "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">{t('footer.stayConnected')}</h3>
            <p className="text-gray-400 mb-8">{t('footer.newsletterDesc')}</p>
            {isSubscribed ? (
              <div className="flex items-center justify-center gap-2 text-green-400">
                <CheckCircle size={24} />
                <span className="text-lg font-medium">{t('footer.subscribed')}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder={t('footer.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-600"
                  data-testid="newsletter-email-input"
                />
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 min-w-[140px]"
                  data-testid="newsletter-submit-btn"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('footer.subscribe')
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="rounded-full bg-white shadow-lg ring-2 ring-amber-500/30 overflow-hidden">
                <img 
                  src="/logo-circle.png"
                  alt="Jerusalem Holy Prayer"
                  className="h-12 w-12 object-cover"
                />
              </div>
              <div>
                <div className="font-bold text-base text-white">JERUSALEM</div>
                <div className="text-xs text-gray-400">HOLY PRAYER</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Bridging hearts and sacred ground through the timeless practice of prayer, honoring faith traditions with reverence and authenticity.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-amber-500">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              <li><Link to="/prayer-request" className="text-gray-400 hover:text-white text-sm transition-colors">{t('nav.prayerRequest')}</Link></li>
              <li><Link to="/sacred-sites" className="text-gray-400 hover:text-white text-sm transition-colors">{t('nav.sacredSites')}</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-white text-sm transition-colors">{t('nav.pricing')}</Link></li>
              <li><Link to="/verify" className="text-gray-400 hover:text-white text-sm transition-colors">Verify My Prayer</Link></li>
              <li><Link to="/testimonials" className="text-gray-400 hover:text-white text-sm transition-colors">{t('nav.testimonials')}</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold mb-4 text-amber-500">{t('footer.resources')}</h4>
            <ul className="space-y-2">
              <li><Link to="/pray-at-the-church-of-the-holy-sepulchre" className="text-gray-400 hover:text-white text-sm transition-colors">Pray at the Holy Sepulchre</Link></li>
              <li><Link to="/pray-at-the-western-wall" className="text-gray-400 hover:text-white text-sm transition-colors">Western Wall Prayer Note</Link></li>
              <li><Link to="/pray-at-the-garden-tomb" className="text-gray-400 hover:text-white text-sm transition-colors">Pray at the Garden Tomb</Link></li>
              <li><Link to="/pray-on-the-via-dolorosa" className="text-gray-400 hover:text-white text-sm transition-colors">Via Dolorosa Prayer</Link></li>
              <li><Link to="/pray-at-the-church-of-all-nations" className="text-gray-400 hover:text-white text-sm transition-colors">Gethsemane Prayer</Link></li>
              <li><Link to="/pray-at-the-abbey-of-the-dormition" className="text-gray-400 hover:text-white text-sm transition-colors">Abbey of the Dormition</Link></li>
              <li><Link to="/urgent" className="text-gray-400 hover:text-white text-sm transition-colors">Emergency Rush Delivery</Link></li>
              <li><Link to="/prayer-library" className="text-gray-400 hover:text-white text-sm transition-colors">Prayer Library</Link></li>
              <li><Link to="/prayers-for-healing" className="text-gray-400 hover:text-white text-sm transition-colors">Prayers for Healing</Link></li>
              <li><Link to="/prayers-for-family" className="text-gray-400 hover:text-white text-sm transition-colors">Prayers for Family</Link></li>
              <li><Link to="/holy-word" className="text-gray-400 hover:text-white text-sm transition-colors">Divine Word</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">Prayer Guides</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-white text-sm transition-colors">About Us</Link></li>
              <li><Link to="/faq" className="text-gray-400 hover:text-white text-sm transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="font-semibold mb-4 text-amber-500">{t('footer.connectWithUs')}</h4>
            <div className="flex space-x-3 mb-4">
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 hover:bg-amber-600 flex items-center justify-center transition-colors" aria-label="Facebook">
                <Facebook size={18} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 hover:bg-amber-600 flex items-center justify-center transition-colors" aria-label="Instagram">
                <Instagram size={18} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-800 hover:bg-amber-600 flex items-center justify-center transition-colors" aria-label="Twitter">
                <Twitter size={18} />
              </a>
              <a
                href="mailto:info@jerusalemholyprayer.org"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-amber-600 flex items-center justify-center transition-colors"
                aria-label="Email us"
                data-testid="footer-email-icon"
              >
                <Mail size={18} />
              </a>
            </div>
            <a
              href="mailto:info@jerusalemholyprayer.org"
              className="inline-flex items-center gap-2 text-gray-300 hover:text-amber-400 text-sm font-medium transition-colors break-all"
              data-testid="footer-email-link"
            >
              <Mail size={14} />
              info@jerusalemholyprayer.org
            </a>
            <p className="text-gray-500 text-xs mt-2">
              We typically respond within 24 hours.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              {t('footer.copyright')}
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">{t('footer.privacy')}</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">{t('footer.terms')}</Link>
              <Link to="/refund" className="text-gray-400 hover:text-white text-sm transition-colors">Refund Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;